import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();
const TOKEN_KEY = 'bta_auth_token';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const setToken = useCallback((t) => {
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
    setTokenState(t);
  }, []);

  const authFetch = useCallback(async (path, options = {}) => {
    const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const resp = await fetch(`${BACKEND}${path}`, { ...options, headers });
    return resp;
  }, [token]);

  const fetchMe = useCallback(async () => {
    if (!token) {
      setUser(null);
      setIsLoading(false);
      return null;
    }
    try {
      const resp = await fetch(`${BACKEND}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setUser(data.user);
        setIsLoading(false);
        return data.user;
      }
      // Token invalid / expired
      setToken(null);
      setUser(null);
    } catch (e) {
      console.error('fetchMe error', e);
    }
    setIsLoading(false);
    return null;
  }, [token, setToken]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const register = async ({ email, password, first_name }) => {
    const resp = await fetch(`${BACKEND}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, first_name }),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(formatErr(data?.detail) || 'Could not create your account');
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const login = async ({ email, password }) => {
    const resp = await fetch(`${BACKEND}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(formatErr(data?.detail) || 'Login failed');
    if (data.requires_2fa) {
      // Return challenge; caller must prompt for OTP and call verify2FA
      return { requires2FA: true, challengeToken: data.challenge_token };
    }
    setToken(data.token);
    setUser(data.user);
    return { requires2FA: false, user: data.user };
  };

  const verify2FA = async ({ challengeToken, code }) => {
    const resp = await fetch(`${BACKEND}/api/auth/2fa-verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ challenge_token: challengeToken, code }),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(formatErr(data?.detail) || 'That code did not match.');
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const setup2FA = async () => {
    const resp = await authFetch('/api/auth/2fa-setup', { method: 'POST' });
    const data = await resp.json();
    if (!resp.ok) throw new Error(formatErr(data?.detail) || 'Could not start 2FA setup');
    return data;
  };

  const enable2FA = async (code) => {
    const resp = await authFetch('/api/auth/2fa-enable', { method: 'POST', body: JSON.stringify({ code }) });
    const data = await resp.json();
    if (!resp.ok) throw new Error(formatErr(data?.detail) || 'Could not enable 2FA');
    await fetchMe();
    return data;
  };

  const disable2FA = async () => {
    const resp = await authFetch('/api/auth/2fa-disable', { method: 'POST' });
    const data = await resp.json();
    if (!resp.ok) throw new Error(formatErr(data?.detail) || 'Could not disable 2FA');
    await fetchMe();
    return data;
  };

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, [setToken]);

  const forgotPassword = async (email) => {
    const resp = await fetch(`${BACKEND}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(formatErr(data?.detail) || 'Something went wrong');
    return data;
  };

  const resetPassword = async ({ token: resetToken, new_password }) => {
    const resp = await fetch(`${BACKEND}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: resetToken, new_password }),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(formatErr(data?.detail) || 'Could not reset password');
    return data;
  };

  const verifyEmail = async (verifyToken) => {
    const resp = await fetch(`${BACKEND}/api/auth/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: verifyToken }),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(formatErr(data?.detail) || 'Email confirmation failed');
    // Refresh user state
    await fetchMe();
    return data;
  };

  const resendVerification = async (email) => {
    const resp = await fetch(`${BACKEND}/api/auth/resend-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return resp.ok;
  };

  const claimStripe = async () => {
    const resp = await authFetch('/api/user/claim-stripe', { method: 'POST' });
    const data = await resp.json();
    if (!resp.ok) throw new Error(formatErr(data?.detail) || 'Could not claim access');
    await fetchMe();
    return data;
  };

  const refreshUser = fetchMe;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin',
      isLoading,
      authFetch,
      register,
      login,
      verify2FA,
      setup2FA,
      enable2FA,
      disable2FA,
      logout,
      forgotPassword,
      resetPassword,
      verifyEmail,
      resendVerification,
      claimStripe,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

function formatErr(detail) {
  if (!detail) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}
